import { Injectable, Inject } from '@angular/core';
import { Response, XHRBackend, RequestOptions, RequestOptionsArgs,
         URLSearchParams, Headers, Http } from '@angular/http';
import { Auth } from '@shared/auth';
import { DATASTORE_CONFIG } from './datastore.config';
import { DatastoreConfig } from './datastore.interface';
import { BackendConfig, Params } from './backend';
import { Pagination } from './store';
import { Observable } from 'rxjs/Observable';

export type ApiResponseStatus = 'success' | 'error';

export interface ApiResponse {
  result: Object;
  status: ApiResponseStatus;
}

@Injectable()
export class ApiHttp extends Http {
  constructor(backend: XHRBackend,
    defaultOptions: RequestOptions,
    private auth: Auth,
    @Inject(DATASTORE_CONFIG) private datastoreConfig: DatastoreConfig,
  ) {
    super(backend, defaultOptions);
  }

  fetch(config: BackendConfig, pagination?: Pagination): Observable<Response> {
    return Observable.of(config)
      .withLatestFrom(this.authHeader())
      .switchMap(([newConfig, authHeader]) => {
        const baseUrl = newConfig.isGaf
          ? this.datastoreConfig.ajaxBaseUrl
          : this.datastoreConfig.apiBaseUrl;
        const search = pagination
          ? {
              ...newConfig.params,
              limit: pagination.limit,
              offset: pagination.offset
            }
          : newConfig.params;
        return super.get(
          `${baseUrl}/${newConfig.endpoint}`,
          {
            headers: authHeader,
            search: this.serialize(search)
          }
        );
      })
      .map((res: Response) => res.json())
      .map((body: ApiResponse) => body.result);
  }

  post(config: any, payload: Object): Observable<Response> {
    return Observable.of(config)
      .withLatestFrom(this.authHeader(), (c, h) => [c, h])
      .switchMap(([newConfig, authHeader]) => {
        return super.post(
          `${this.datastoreConfig.apiBaseUrl}/${newConfig.endpoint}`,
          this.serialize({ ...payload, ...newConfig.params }),
          {
            headers: authHeader,
          }
        );
      })
      .map((res: Response) => res.json())
      .map((body: ApiResponse) => body.result);
  }

  private authHeader(): Observable<Headers> {
    return this.auth.map(({uid , token}) => {
      return new Headers({ 'freelancer-auth-v2': `${uid};${token}` });
    });
  }



  private serialize(obj: Partial<Params>): URLSearchParams {
    const params: URLSearchParams = new URLSearchParams();
    Object.entries(obj).forEach(([key, value]) => {
      if (typeof value === 'string') {
        params.set(key, value);
      } else if (typeof value === 'number') {
        params.set(key, String(value));
      } else if (Array.isArray(value)) {
        // This type casting shouldn't be necessary but is because of a TS bug.
        // https://github.com/Microsoft/TypeScript/issues/15315
        // https://github.com/Microsoft/TypeScript/issues/7294

        // Our backend expects arrays foo=[1,2] to be encoded as foo[]=1&foo[]=2
        (value as (string|number)[]).forEach(v => {
          params.append(`${key}[]`, String(v));
        });
      }
    });
    params.set('compact', 'true');
    return params;
  }

  private handleException(error: Response | any) {
    // In a real world app, we might use a remote logging infrastructure
    let errMsg: string;
    if (error instanceof Response) {
      const body = error.json() || '';
      const err = body.error || JSON.stringify(body);
      errMsg = `${error.status} - ${error.statusText || ''} ${err}`;
    } else {
      errMsg = error.message ? error.message : error.toString();
    }
    console.error(errMsg);
    return Observable.throw(errMsg);
  }
}



// WEBPACK FOOTER //
// ./src/app/@shared/datastore/api-http.service.ts